/**
 * R&P admin action catalogs per operational module (API `actionName`, lowercased).
 * Used by {@link isActionInModuleRpeCatalog}: actions **not** in a module's catalog
 * are not R&P-gated (remain accessible). Actions **in** the catalog require a grant.
 *
 * Keep in sync with Roles & Permissions module action checkboxes / loadEffectivePermissions.
 */

function toActionSet(actions: readonly string[]): ReadonlySet<string> {
  return new Set(actions.map((action) => action.trim().toLowerCase()));
}

const COMMON_SUMMARY_ACTIONS = [
  'add',
  'view',
  'edit',
  'delete',
  'bulk view',
  'bulk edit',
  'bulk delete',
  'print',
] as const;

const BAG_RPE_ACTIONS = toActionSet([
  ...COMMON_SUMMARY_ACTIONS,
  'download file',
  'link shipment',
  'delink shipment',
  'bulk manifest',
  'gateway, bag and manifest by incoming container',
  'send for clearance',
  'scan shipments',
]);

const MANIFEST_RPE_ACTIONS = toActionSet([
  ...COMMON_SUMMARY_ACTIONS,
  'download file',
  'link bag',
  'delink bag',
  'link shipment',
  'delink shipment',
  'publish',
  'send for clearance',
  'scan shipments',
  'bulk mawb',
]);

const MAWB_RPE_ACTIONS = toActionSet([
  ...COMMON_SUMMARY_ACTIONS,
  'download file',
  'add manifest',
  'remove manifest',
  'publish',
  'publish mawb with partial bags',
  'send for clearance',
]);

const AP_RPE_ACTIONS = toActionSet([
  ...COMMON_SUMMARY_ACTIONS,
  'upload ap charges',
  'download charge sheet',
  'download ap invoices',
  'download file',
  'publish to gp',
  'load ap invoices from ftp / sftp',
  'republish to gp',
  'override ap invoice date limits',
]);

const AR_RPE_ACTIONS = toActionSet([
  ...COMMON_SUMMARY_ACTIONS,
  'download ar invoices',
  'download file',
  'publish invoices',
  'publish invoice to customer',
  'republish',
  'republish to customer',
]);

/** moduleName (API) → configurable action names. Aliases share the same set. */
export const MODULE_RPE_ACTION_CATALOG: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  Bag: BAG_RPE_ACTIONS,
  Manifest: MANIFEST_RPE_ACTIONS,
  MAWB: MAWB_RPE_ACTIONS,
  'Account Payable': AP_RPE_ACTIONS,
  'Accounts Payable': AP_RPE_ACTIONS,
  'Account Receivable': AR_RPE_ACTIONS,
  'Accounts Receivable': AR_RPE_ACTIONS,
};

/**
 * @returns `true` if action is a configurable R&P action for the module;
 *          `false` if the module has a catalog and the action is absent (do not gate);
 *          `null` if the module has no catalog (legacy deny-if-ungranted).
 */
export function isActionInModuleRpeCatalog(
  moduleName: string,
  actionName: string
): boolean | null {
  const catalog = MODULE_RPE_ACTION_CATALOG[moduleName];
  if (!catalog) {
    return null;
  }
  return catalog.has(actionName.trim().toLowerCase());
}
